import { StateCreator } from 'zustand';
import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';
// import { createWithEqualityFn } from 'zustand/traditional';
import { immer } from 'zustand/middleware/immer';
// import { createJSONStorage, persist } from 'zustand/middleware';
import {
  createChatSlice, ChatSlice, ChatId, Messages,
} from '@/stores/slices/chatSlice';
import {
  createGuideSlice, GuideSlice,
} from '@/stores/slices/guideSlice';
import {
  createMindSlice, MindSlice,
} from '@/stores/slices/mindSlice';
// import {
//   createNoteSlice, NoteSlice,
// } from '@/stores/slices/noteSlice';
import {
  createPaperSlice,
  FetchPaperMode,
  FetchPaperOrder,
  getListCitationStyleApiKey,
  PaperId,
  PaperSlice,
} from '@/stores/slices/paperSlice';
import {
  createRuntimeSlice, RuntimeSlice,
} from '@/stores/slices/runtimeSlice';
import {
  createSubjectSlice, Subject, SubjectSlice,
} from '@/stores/slices/subjectSlice';
import {
  createSummarizeSlice, SummarizeSlice,
} from '@/stores/slices/summarizeSlice';
import {
  createUserSlice, UserSlice,
} from '@/stores/slices/userSlice';
import {
  createPaymentSlice,
  PaymentSlice,
} from '@/stores/slices/paymentSlice';
import {
  createInvoiceSlice,
  InvoiceSlice,
} from '@/stores/slices/invoiceSlice';
import { CommonSlice, createCommonSlice } from '@/stores/slices/commonSlice';
import {
  createWorkflowSlice,
  WorkflowSlice,
} from '@/stores/slices/workflowSlice';
import {
  createUploadSessionSlice,
  UploadSessionSlice,
} from '@/stores/slices/uploadSessionSlice';
import { createSelectionSlice, SelectionSlice } from './slices/selectionSlice';
import { createTranslationSlice, TranslationSlice } from '@/stores/slices/translationSlice';
import { createPaperSearchSlice, PaperSearchSlice } from '@/stores/slices/paperSearchSlice';
import { createResearchDrawingSlice, ResearchDrawingSlice } from '@/stores/slices/researchDrawingSlice';
import { useCallback, useEffect, useMemo } from 'react';

export type CombinedState = {}
  & CommonSlice
  & ChatSlice
  & GuideSlice
  & MindSlice
  // & NoteSlice
  & PaperSlice
  & PaymentSlice
  & InvoiceSlice
  & RuntimeSlice
  & SubjectSlice
  & SummarizeSlice
  & UserSlice
  & WorkflowSlice
  & SelectionSlice
  & TranslationSlice
  & UploadSessionSlice
  & PaperSearchSlice
  & ResearchDrawingSlice

export type StateSlice<T> = StateCreator<
  CombinedState,
  [['zustand/immer', never]],
  [],
  T
>;

export const useStore = createWithEqualityFn<CombinedState>()(
  // persist(
  immer((...a) => ({
    ...createChatSlice(...a),
    ...createCommonSlice(...a),
    ...createGuideSlice(...a),
    ...createMindSlice(...a),
    // ...createNoteSlice(...a),
    ...createPaperSlice(...a),
    ...createPaymentSlice(...a),
    ...createInvoiceSlice(...a),
    ...createRuntimeSlice(...a),
    ...createSubjectSlice(...a),
    ...createSummarizeSlice(...a),
    ...createUserSlice(...a),
    ...createWorkflowSlice(...a),
    ...createSelectionSlice(...a),
    ...createTranslationSlice(...a),
    ...createUploadSessionSlice(...a),
    ...createPaperSearchSlice(...a),
    ...createResearchDrawingSlice(...a),
  })),
)

export const useUser = () => useStore((state) => ({
  user: state.user,
  userQuotas: state.userQuotas,
  recoveryOffer: state.recoveryOffer,
  firstOffer: state.firstOffer,
  expireVipIncentiveOffer: state.expireVipIncentiveOffer,
  refreshUserInfo: state.refreshUserInfo,
  vipUpgradePreview: state.vipUpgradePreview,
  vipUpgradePreviewLoading: state.vipUpgradePreviewLoading,
  fetchVipUpgradePreview: state.fetchVipUpgradePreview,
  fetchBalancesLoading: state.fetchBalancesLoading,
  fetchBalances: state.fetchBalances,
  // 余额不足提醒
  balanceInsufficient: state.balanceInsufficient,
  setBalanceInsufficient: state.setBalanceInsufficient,
  // 微信解绑
  unbindWechat: state.unbindWechat,
  // 微信绑定
  bindWechat: state.bindWechat,
  getCouponList: state.getCouponList,
}), shallow);

export const useUserVip = () => useStore((state) => ({
  ...state.userVip,
}), shallow);

// 飞豆消费点数
export const useConsumptions = () => useStore((state) => ({
  consumptions: state.consumptions,
  initialConsumptions: state.initialConsumptions,
}), shallow);

export const usePapers = (subjectId: string = '', mode?: FetchPaperMode) => {
  const basePapers = useStore(state => state.papers);
  const getPapersData = useStore(state => state.getPapersData);
  const initPapersData = useStore(state => state.initPapersData);

  // 在 useEffect 中初始化数据，避免在渲染期间更新状态
  useEffect(() => {
    if (mode !== 'base' && subjectId) {
      initPapersData(subjectId);
    }
  }, [subjectId, mode, initPapersData]);

  if (mode === 'base') {
    return basePapers;
  }

  return getPapersData(subjectId).papers;
};

// 翻页
export const usePagination = (subjectId: string = '') => {
  const getPapersData = useStore(state => state.getPapersData);
  const getPagination = useStore(state => state.getPagination);
  const initPapersData = useStore(state => state.initPapersData);

  // 在 useEffect 中初始化数据，避免在渲染期间更新状态
  useEffect(() => {
    if (subjectId) {
      initPapersData(subjectId);
    }
  }, [subjectId, initPapersData]);

  const papersData = getPapersData(subjectId);
  const pagination = getPagination(subjectId);

  return {
    totalCounts: papersData.total,      // 直接返回值，不是函数
    pageSize: papersData.pageSize,      // 直接返回值，不是函数
    pageIndex: papersData.pageIndex,    // 直接返回值，不是函数
    ...pagination
  };
};

// 过滤器
export const useFilters = (subjectId: string = '') => {
  const getPapersData = useStore(state => state.getPapersData);
  const setSearchStore = useStore(state => state.filters.setSearch);
  const setOrder = useStore(state => state.filters.setOrder);
  const initPapersData = useStore(state => state.initPapersData);

  // 在 useEffect 中初始化数据，避免在渲染期间更新状态
  useEffect(() => {
    if (subjectId) {
      initPapersData(subjectId);
    }
  }, [subjectId, initPapersData]);

  const subjectPapersData = getPapersData(subjectId);

  return {
    searchString: subjectPapersData.search,
    order: subjectPapersData.order,
    setSearch: useCallback((search: string) => (
      setSearchStore(search, subjectId)
    ), [setSearchStore, subjectId]),
    setOrder: useCallback((order: FetchPaperOrder) => (
      setOrder(order, subjectId)
    ), [setOrder, subjectId]),
  };
};

export const useFetchPapers = () => useStore((state) => ({
  getFetchedStatus: state.getPapersFetchedStatus,
  fetchLoading: state.fetchPapersLoading,
  setFetchPapersLoading: state.setFetchPapersLoading,
  fetchSilent: state.fetchPapersSilent,
  fetchPapers: state.fetchPapers,
}), shallow);

export const usePapersCitationStyle = (subjectId: string = '') => useStore(
  (state) => state.getPapersData(subjectId).citationStyle,
);

export const usePapersPinQuota = (subjectId: string = '') => useStore((state) => {
  const papersData = state.getPapersData(subjectId);
  return {
    pinLimit: papersData.pinLimit ?? null,
    pinnedCount: papersData.pinnedCount ?? null,
  };
}, shallow);

export const useListCitationStyleKey = (subjectId: string = '') => useStore((state) => {
  const storeKey = subjectId || 'COMMON';
  const persistedKey = state.listCitationStyleKeys[storeKey];
  if (persistedKey) return persistedKey;
  const papersData = state.papersData[storeKey];
  if (!papersData) return null;
  return papersData.citationStyleKey ?? getListCitationStyleApiKey(papersData.citationStyle);
});

export const useListCitationStyleUseDefault = (subjectId: string = '') => useStore((state) => {
  const storeKey = subjectId || 'COMMON';
  return state.listCitationStyleUseDefault[storeKey] ?? false;
});

export const usePapersData = () => useStore((state) => ({
  papersData: state.papersData,
}), shallow);

export const usePaper = (paperId?: PaperId) => {
  const paper = useStore((state) => (paperId && state.paperRecords[paperId]));
  return paper || null;
};

export const useFetchPaper = () => useStore((state) => ({
  fetchLoading: state.fetchPaperLoading,
  fetchPaper: state.fetchPaper,
}), shallow);

export const useChat = (chatId?: ChatId) => useStore((state) => (
  chatId ? state.chats[chatId] : [] as Messages
));

// export const useNotes = () => useStore((state) => ({
//   ...state.notes
// }));

export const useSummarizes = () => useStore((state) => ({
  ...state.summarizes
}), shallow);

export const useWorkflows = () => useStore((state) => ({
  ...state.workflows
}), shallow);

export const useSubjects = () => useStore((state => ({
  ...state.subjects,
})), shallow);

export const useRuntime = () => useStore((state => ({
  ...state.runtime
})), shallow);

export const useUpload = () => useStore(state => ({
  uploadImages: state.uploadImages,
}), shallow);

export const useGuides = () => useStore(state => ({
  touchpointItems: state.touchpointItems,
  touchpointSlots: state.touchpointSlots,
  globalTouchpointItems: state.globalTouchpointItems,
  globalTouchpointSlots: state.globalTouchpointSlots,
  currentTouchpointsPage: state.currentTouchpointsPage,
  fetchTouchpoints: state.fetchTouchpoints,
  fetchGlobalTouchpoints: state.fetchGlobalTouchpoints,
  dismissTouchpoint: state.dismissTouchpoint,
  clearTouchpoints: state.clearTouchpoints,
}), shallow);

export const useAssistants = () => useStore(state => ({
  assistants: state.assistants,
  initialAssistants: state.initialAssistants,
  assistantsAILoading: state.assistantsAILoading,
  assistantsAIGenerated: state.assistantsAIGenerated,
  assistantsOutput: state.assistantsOutput,
  updateAssistantsOutput: state.updateAssistantsOutput,
}), shallow);

export const useResearchDrawing = () => useStore(state => ({
  ...state.researchDrawing,
}), shallow);

// 阶梯价格
export const useLadderPrices = () => useStore(state => ({
  ladderPrices: state.ladderPrices,
}), shallow);
// 阶梯VIP开通
export const usePaymentUpgrade = () => useStore(state => ({
  ...state.paymentUpgradeSet,
}), shallow);

export const usePaymentUpgradePlus3x = () => useStore(state => ({
  ...state.paymentUpgradeSetPlus3x,
}), shallow);

export const usePayment = () => useStore(state => ({
  ...state.payment
}), shallow);

export const useInvoice = () => useStore(state => ({
  ...state.invoice
}), shallow);

// 常见问题
export const useCommonProblems = () => useStore(state => ({
  commonProblems: state.commonProblems,
}), shallow);

// 邀请活动
export const useActivities = () => useStore(state => ({
  ...state.activities
}), shallow);

// 全局配置
export const useGlobalConfig = () => useStore(state => ({
  globalConfig: state.globalConfig,
}), shallow);

// 首页关注我们
export const useFollowUsList = () => useStore(state => ({
  followUsList: state.followUsList,
}), shallow);

// 全局配置
export const useShortUrlMap = () => useStore(state => ({
  shortUrlMap: state.shortUrlMap,
}), shallow);

// 你知道吗
export const useDidYouKnowConfig = () => useStore(state => ({
  ...state.didYouKnowConfig,
}), shallow);

// 系统公告（仅订阅展示字段，避免 loading/value 变更导致多余重渲染）
export const useSystemAnnouncementDisplay = () => useStore(
  (state) => ({
    content: state.systemAnnouncement.content,
    linkto: state.systemAnnouncement.linkto,
  }),
  shallow,
);

export function syncSystemAnnouncementForPath(pathname: string) {
  return useStore.getState().systemAnnouncement.getContent(pathname);
}

// 系统维护
export const useSystemMaintenance = () => useStore(state => ({
  ...state.systemMaintenance,
}), shallow);

// 反馈
export const useFeedback = () => useStore(state => ({
  ...state.feedback,
}), shallow);

// AI 列
export const useAiColsConfig = () => useStore(state => ({
  ...state.aiColsConfig,
}), shallow);

// 默认章节
export const useChapterTocConfig = () => useStore(state => ({
  ...state.chapterTocConfig,
}), shallow);

export const usePaperMetadata = () => useStore(state => ({
  currentPaperMetadataByDoi: state.currentPaperMetadataByDoi,
  clearCurrentPaperMetadataByDoi: state.clearCurrentPaperMetadataByDoi,
  fetchPaperMetadataLoading: state.fetchPaperMetadataLoading,
  currentPaperMetadataByDoiError: state.currentPaperMetadataByDoiError,
  fetchPaperMetadataByDoi: state.fetchPaperMetadataByDoi,
  savePaperMetadata: state.savePaperMetadata,
}), shallow);

// 日志
export const useEventLog = () => useStore(state => ({
  eventLog: state.eventLog,
}), shallow);

// 引用格式配置
export const useCitationStylesConfig = () => useStore(state => ({
  ...state.citationStylesConfig,
}), shallow);

// 大纲 Agent 常用指令配置
export const useOutlineAgentPromptConfig = () => useStore(state => ({
  ...state.outlineAgentPromptConfig,
}), shallow);

export const useAiCol = () => useStore(state => ({
  colConsumePrice: state.colConsumePrice,
  setColConsumePrice: state.setColConsumePrice,
  refreshPaperCol: state.refreshPaperCol,
  fetchPaperCols: state.fetchPaperCols,
  setCitationStyle: state.setCitationStyle,
  setPaperCols: state.setPaperCols,
  preCreateAiCol: state.preCreateAiCol,
  createAiCol: state.createAiCol,
  deleteAiCol: state.deleteAiCol,
}), shallow);

// AI 列
export const useCols = (subjectId: string = '') => {
  const getPapersData = useStore(state => state.getPapersData);
  const initPapersData = useStore(state => state.initPapersData);

  // 在 useEffect 中初始化数据，避免在渲染期间更新状态
  useEffect(() => {
    if (subjectId) {
      initPapersData(subjectId);
    }
  }, [subjectId, initPapersData]);

  return getPapersData(subjectId).cols;
};

// 全文对照翻译
export const useTranslation = () => {
  return useStore((state) => ({
    createTranslate: state.transition.createTranslate,
    fetchTranslateDetail: state.transition.fetchTranslateDetail,
    fetchTranslateList: state.transition.fetchTranslateList,
    exportTranslatedResult: state.transition.exportTranslatedResult,
    fetchTranslateDetailDebounced: state.transition.fetchTranslateDetailDebounced,
    translateText: state.transition.translateText,
  }), shallow);
};

export const useUploadSessions = () => useStore((state) => ({
  uploadSessions: state.uploadSessions,
  activeUploadSessionId: state.activeUploadSessionId,
  createUploadSession: state.createUploadSession,
  getOrCreateUploadSession: state.getOrCreateUploadSession,
  setActiveUploadSession: state.setActiveUploadSession,
  addUploadTasks: state.addUploadTasks,
  updateUploadTask: state.updateUploadTask,
  removeUploadTask: state.removeUploadTask,
  clearUploadSession: state.clearUploadSession,
  getUploadSessionTasks: state.getUploadSessionTasks,
  getAllUploadTasks: state.getAllUploadTasks,
}), shallow);
